// e1c/src/pages/CreateOrganizationPage.js
import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  Grid,
  Link,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import logger from '../utils/logger';
import authFetch from '../utils/authFetch'; // Import authFetch

// Use REACT_APP_API_BASE_URL which should point to the base, e.g., https://api.medsai.us/api
// Note: Some environments might set this to REACT_APP_API_BASE_URL. Ensure it matches your setup.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const NPI_REGEX = /^\d{10}$/; // Regex for NPI validation

const steps = ['Organization Details', 'Administrator Details'];

function CreateOrganizationPage() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  // State for form fields
  const [organizationName, setOrganizationName] = useState('');
  const [organizationNpi, setOrganizationNpi] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminConfirmPassword, setAdminConfirmPassword] = useState('');
  const [adminNpi, setAdminNpi] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // General State
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);
  const handleMouseDownConfirmPassword = (event) => event.preventDefault();

  const validateStep = () => {
    const newFieldErrors = {};
    let isValid = true;

    if (activeStep === 0) { // Validate Organization Details
      if (!organizationName.trim()) {
        newFieldErrors.organizationName = 'Organization name is required.';
        isValid = false;
      }
      if (!organizationNpi.trim()) {
        newFieldErrors.organizationNpi = 'Organization NPI is required.';
        isValid = false;
      } else if (!NPI_REGEX.test(organizationNpi)) {
        newFieldErrors.organizationNpi = 'NPI must be exactly 10 digits.';
        isValid = false;
      }
    } else if (activeStep === 1) { // Validate Administrator Details
      if (!adminFirstName.trim()) { newFieldErrors.adminFirstName = 'Admin first name is required.'; isValid = false; }
      if (!adminLastName.trim()) { newFieldErrors.adminLastName = 'Admin last name is required.'; isValid = false; }
      if (!adminEmail.trim()) {
        newFieldErrors.adminEmail = 'Admin email is required.';
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail)) {
        newFieldErrors.adminEmail = 'Invalid email format.';
        isValid = false;
      }
      if (!adminPassword) {
        newFieldErrors.adminPassword = 'Admin password is required.';
        isValid = false;
      } else if (adminPassword.length < 8) {
        newFieldErrors.adminPassword = 'Password must be at least 8 characters.';
        isValid = false;
      }
      if (adminPassword !== adminConfirmPassword) { newFieldErrors.adminConfirmPassword = 'Passwords do not match.'; isValid = false; }
      if (!adminNpi.trim()) {
        newFieldErrors.adminNpi = 'Admin NPI is required.';
        isValid = false;
      } else if (!NPI_REGEX.test(adminNpi)) {
        newFieldErrors.adminNpi = 'NPI must be exactly 10 digits.';
        isValid = false;
      }
    }
    setFieldErrors(newFieldErrors);
    return isValid;
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setError('');
      setFieldErrors({});
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError('');
    setFieldErrors({});
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateStep()) {
      logger.warn("Validation failed before submitting organization creation.");
      return;
    }
    setError('');
    setFieldErrors({});
    setLoading(true);
    logger.info(`Attempting organization registration for: ${organizationName}`);

    const payload = {
      organization_name: organizationName,
      organization_npi: organizationNpi,
      admin_first_name: adminFirstName,
      admin_last_name: adminLastName,
      admin_email: adminEmail,
      admin_password: adminPassword,
      admin_npi: adminNpi,
    };

    try {
      // --- START: MODIFIED BLOCK ---
      // Use the authFetch utility for consistency and correct URL construction.
      // The endpoint path must now include /api/ as expected by authFetch's publicEndpoints list.
      const responseData = await authFetch('/api/auth/register-tenant', {
        method: 'POST',
        body: payload,
      });
      // --- END: MODIFIED BLOCK ---

      logger.info("Organization registration successful.", responseData);
      navigate('/login?registrationSuccess=true');

    } catch (err) {
      logger.error("Error during organization registration submission:", err);
      setError(err.message || 'Registration failed. An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    // This function's content remains the same
    switch (step) {
      case 0:
        return (
          <>
            <Typography component="h2" variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
              Organization Details
            </Typography>
            <TextField margin="normal" required fullWidth id="organizationName" label="Organization Name" name="organizationName" autoComplete="organization" autoFocus value={organizationName} onChange={(e) => setOrganizationName(e.target.value)} disabled={loading} error={!!fieldErrors.organizationName} helperText={fieldErrors.organizationName} />
            <TextField margin="normal" required fullWidth id="organizationNpi" label="Organization NPI (10 digits)" name="organizationNpi" value={organizationNpi} onChange={(e) => setOrganizationNpi(e.target.value)} disabled={loading} error={!!fieldErrors.organizationNpi} helperText={fieldErrors.organizationNpi} />
          </>
        );
      case 1:
        return (
          <>
            <Typography component="h2" variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
              Administrator Account Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField margin="normal" required fullWidth id="adminFirstName" label="Admin First Name" name="adminFirstName" autoComplete="given-name" value={adminFirstName} onChange={(e) => setAdminFirstName(e.target.value)} disabled={loading} error={!!fieldErrors.adminFirstName} helperText={fieldErrors.adminFirstName} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField margin="normal" required fullWidth id="adminLastName" label="Admin Last Name" name="adminLastName" autoComplete="family-name" value={adminLastName} onChange={(e) => setAdminLastName(e.target.value)} disabled={loading} error={!!fieldErrors.adminLastName} helperText={fieldErrors.adminLastName} />
              </Grid>
            </Grid>
            <TextField margin="normal" required fullWidth id="adminEmail" label="Admin Email Address" name="adminEmail" autoComplete="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} disabled={loading} error={!!fieldErrors.adminEmail} helperText={fieldErrors.adminEmail} />
            <TextField margin="normal" required fullWidth name="adminPassword" label="Admin Password (min. 8 characters)" type={showPassword ? 'text' : 'password'} id="adminPassword" autoComplete="new-password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} disabled={loading} error={!!fieldErrors.adminPassword} helperText={fieldErrors.adminPassword} InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end"> {showPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> ), }} />
            <TextField margin="normal" required fullWidth name="adminConfirmPassword" label="Confirm Admin Password" type={showConfirmPassword ? 'text' : 'password'} id="adminConfirmPassword" autoComplete="new-password" value={adminConfirmPassword} onChange={(e) => setAdminConfirmPassword(e.target.value)} disabled={loading} error={!!fieldErrors.adminConfirmPassword} helperText={fieldErrors.adminConfirmPassword} InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton aria-label="toggle confirm password visibility" onClick={handleClickShowConfirmPassword} onMouseDown={handleMouseDownConfirmPassword} edge="end"> {showConfirmPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> ), }} />
            <TextField margin="normal" required fullWidth id="adminNpi" label="Admin NPI (10 digits)" name="adminNpi" value={adminNpi} onChange={(e) => setAdminNpi(e.target.value)} disabled={loading} error={!!fieldErrors.adminNpi} helperText={fieldErrors.adminNpi} />
          </>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }} >
        <Typography component="h1" variant="h5" gutterBottom>
          Register Your Organization
        </Typography>

        <Stepper activeStep={activeStep} sx={{ width: '100%', mb: 3 }}>
          {steps.map((label) => ( <Step key={label}> <StepLabel>{label}</StepLabel> </Step> ))}
        </Stepper>

        {error && ( <Alert severity="error" sx={{ width: '100%', mt: 2, mb: 2 }}> {error} </Alert> )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
          {getStepContent(activeStep)}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button disabled={activeStep === 0 || loading} onClick={handleBack} variant="outlined"> Back </Button>
            {activeStep === steps.length - 1 ? (
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Register Organization'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext} disabled={loading}> Next </Button>
            )}
          </Box>
          <Grid container justifyContent="center" sx={{ mt: 3 }}>
            <Grid item>
              <Link component={RouterLink} to="/login" variant="body2">
                Already have an account? Sign In
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
}

export default CreateOrganizationPage;